import type { UseTabNavigationProps } from "./use-tab-navigation.type";

import { useEffect } from "react";
import { useLocation, useSearchParams } from "react-router";

export function useTabNavigation({
  items,
  tabKey,
  defaultActiveKey,
  paramsToKeep = [],
  alwaysSyncSearchParams = false,
  shouldSync,
}: UseTabNavigationProps) {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter hidden tabs
  const visibleTabs = items.filter((tab) => !tab.hidden);

  // Determine active tab
  const activeTab = searchParams.has(tabKey)
    ? items?.find(
        (childTab) =>
          childTab.key.toLowerCase() ===
          searchParams?.get(tabKey)?.toLowerCase()
      )
    : undefined;

  const activeTabKey =
    activeTab?.key ?? defaultActiveKey ?? visibleTabs[0]?.key;

  // Set tab in URL
  const setTab = ({
    tabValue,
    replace = false,
  }: {
    tabValue: string;
    replace?: boolean;
  }) => {
    const newParams = new URLSearchParams();

    // Keep specified parameters
    if (paramsToKeep?.length) {
      for (const paramKey of paramsToKeep) {
        const paramValue = searchParams.get(paramKey);
        if (paramValue) newParams.set(paramKey, paramValue);
      }
    }

    // Set tab parameter
    newParams.set(tabKey, tabValue);

    // Update search parameters
    setSearchParams(newParams, {
      replace,
      state: { from: location.state?.from },
    });
  };

  // Sync URL with active tab if needed
  useEffect(() => {
    if (!alwaysSyncSearchParams) return;
    if (shouldSync && !shouldSync(searchParams)) return;

    const currentUrlTabValue = searchParams.get(tabKey);

    if (!currentUrlTabValue && activeTabKey) {
      setTab({ tabValue: activeTabKey, replace: true });
    }
  }, [alwaysSyncSearchParams, searchParams, activeTabKey, shouldSync, tabKey]);

  // Handle tab change
  const onTabChange = (key: string) => {
    const selectedTab = items?.find((childTab) => childTab.key === key);
    if (selectedTab) {
      setTab({ tabValue: selectedTab.key.toLowerCase() });
    }
  };

  return {
    visibleTabs,
    activeTabKey,
    onTabChange,
    location,
  };
}
